import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import {
  collection,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  doc,
  where,
  query,
  getFirestore,
  limit,
} from 'firebase/firestore'
import {
  ref,
  getStorage,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage'

export class Firebase {
  constructor({ apiKey, messagingSenderId, appId, measurementId }) {
    this.apiKey = apiKey
    this.messagingSenderId = messagingSenderId
    this.appId = appId
    this.measurementId = measurementId
  }

  /**
   * Initialize the Firebase database instance.
   * This method should only be called once at the start of the web.
   * @returns The current Firebase instance
   */
  async init() {
    const firebaseConfig = {
      apiKey: this.apiKey,
      authDomain: 'neureader-cait.firebaseapp.com',
      projectId: 'neureader-cait',
      storageBucket: 'neureader-cait.appspot.com',
      messagingSenderId: this.messagingSenderId,
      appId: this.appId,
      measurementId: this.measurementId,
    }
    // Initialize Firebase
    const app = initializeApp(firebaseConfig)
    const analytics = getAnalytics(app)

    this.app = app
    this.analytics = analytics
    this.firestore = getFirestore(this.app)
    this.storage = getStorage(this.app)

    return this
  }

  /**
   * Return ids and all data of documents in a collection
   * @param {String} collectionName The collection name
   * @returns All documents data of that collection
   */
  async documents(collectionName) {
    // , limit(5000)

    const q = query(collection(this.firestore, collectionName), limit(5000))

    // Thực hiện truy vấn và lấy dữ liệu
    const querySnapshot = await getDocs(q)

    // Xử lý dữ liệu
    const documents = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      data: doc.data(),
    }))

    return documents
  }

  /**
   * Return the document instance of the document
   * whose id is {documentId} in {collection}
   * @param {String} collectionName The collection name
   * @param {String} documentId The document's id
   * @returns The document instance
   */
  async document(collectionName, documentId) {
    const querySnapshot = await getDoc(
      doc(this.firestore, collectionName, documentId)
    )
    return new Document({ id: querySnapshot.id, data: querySnapshot.data() })
  }

  /**
   * Set the data for a document.
   * If the document has not existed yet, it will be created.
   * If the document has existed, it will be overriden
   * @param {String} collectionName The name of the collection
   * @param {String} documentId The id of the document
   * @param {Map} data The data in key-value
   */
  async set(collectionName, documentId, data) {
    await setDoc(doc(this.firestore, collectionName, documentId), data)
  }

  /**
   * Update data for a document.
   * If the document has not existed, it will throw an error.
   * If the document has existed, only fields in {data} params will be overriden
   * @param {String} collectionName The name of the collection
   * @param {String} documentId The id of the document
   * @param {Map} data The data in key-value
   */
  async update(collectionName, documentId, data) {
    await updateDoc(doc(this.firestore, collectionName, documentId), data)
  }

  /**
   * Delete an existed document.
   * @param {String} collectionName The collection name
   * @param {String} documentId The id of the document
   */
  async delete(collectionName, documentId) {
    await deleteDoc(doc(this.firestore, collectionName, documentId))
  }

  /**
   * Upload a file into the given path of storage database.
   * @param {String} path The target directory path
   * @param {*} fileBytes The file in uint8 bytes
   * @param {UploadMetadata} metadata The metadata of the file. NULLABLE.
   */
  async uploadFile(path, fileBytes, metadata) {
    const directory = ref(this.storage, path)
    await uploadBytes(directory, fileBytes, metadata)
  }
  async getFile(path) {
    try {
      const fileRef = ref(this.storage, path)

      const downloadURL = await getDownloadURL(fileRef)
      // const filles = await getBlob(fileRef);
      return downloadURL
    } catch (error) {
      console.error('Error getting file from Firebase:', error)
      throw error
    }
  }
  /**
   * Search documents
   * This Fucntion use And Operator
   * @param {string} collection the collection
   * @param {Array} searchParams 3xN Arrays that hold the conditions
   * That can be contruct as [["column", "condition", "value"]]
   * @returns The documents instance
   */
  // array
  // normal
  // async searchDocuments({ collectionName, conditions }) {
  //   let q = collection(this.firestore, collectionName);

  //   conditions.forEach(([field, operator, value]) => {
  //     q = query(q, where(field, operator, value));
  //   });

  //   const querySnapshot = await getDocs(q);
  //   const docs = querySnapshot.docs.map(
  //     (doc) => new Document({ id: doc.id, data: doc.data() })
  //   );
  //   return docs;
  // }
  async queryByEmail(collectionName, email) {
    const q = query(
      collection(this.firestore, collectionName),
      where('email', '==', email)
    )
    const querySnapshot = await getDocs(q)
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0] // Lấy tài liệu đầu tiên (nếu có nhiều tài liệu, bạn có thể xử lý theo cách khác)

      return {
        id: doc.id,
        data: doc.data(),
      }
    } else {
      return null
    }
  }

  async searchDocuments({ collectionName, andConditions, orConditions }) {
    let q = collection(this.firestore, collectionName)

    // Xử lý các điều kiện AND trước
    andConditions.forEach(([field, operator, value]) => {
      q = query(q, where(field, operator, value))
    })

    // Xử lý các điều kiện OR
    let orQueries = []
    if (orConditions && orConditions.length > 0) {
      orConditions.forEach(([field, operator, value]) => {
        let orQuery = query(q, where(field, operator, value))
        orQueries.push(orQuery)
      })
    } else {
      // Nếu không có điều kiện OR thì chỉ chạy query với các điều kiện AND
      orQueries.push(q)
    }

    // Lấy kết quả từ tất cả các truy vấn OR
    let results = []
    for (let orQuery of orQueries) {
      const querySnapshot = await getDocs(orQuery)
      querySnapshot.docs.forEach((doc) => {
        results.push(new Document({ id: doc.id, data: doc.data() }))
      })
    }

    // Loại bỏ các tài liệu trùng lặp
    const uniqueResults = Array.from(
      new Map(results.map((doc) => [doc.id, doc])).values()
    )

    return uniqueResults
  }

  /**
   * Sign-in with SSO.
   *
   * @param {string} email The account email
   * @param {string} password The account password
   * @returns If the login is success, return user's credential.
   * Otherwise, return null.
   */
  async authSSO({ email, password }) {
    try {
      const result = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      )
      return result.user
    } catch (_) {
      return null
    }
  }
  async uploadFilePercent({ path, file, onProgress, onComplete, onError }) {
    if (!path || path === '') {
      throw new Error('Invalid path specified for Firebase Storage')
    }
    const storageRef = ref(this.storage, path)
    const uploadTask = uploadBytesResumable(storageRef, file)

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        onProgress(progress)
      },
      (error) => {
        onError(error)
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
        onComplete(downloadURL)
      }
    )

    return uploadTask
  }
  async signOutUser() {
    await signOut(this.auth)
  }
}

class Document {
  constructor({ id, data }) {
    this.id = id
    this.data = data
  }
}
// class Documents {
//   constructor({ id, data }) {
//     this.id = id;
//     this.data = data;
//   }
// }
